import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// User store with role management and authentication state
const useUserStore = create(
  persist(
    (set, get) => ({
      // Authentication state
      user: null,
      session: null,
      isAuthenticated: false,
      loading: false,
      
      // Role and permissions
      role: 'guest', // 'guest', 'member', 'admin'
      permissions: [],
      membershipTier: 'free', // 'free', 'paid', 'elite'
      
      // Entitlements and features
      entitlements: {
        aiChatMessages: 50,
        botHubAccess: false,
        communityFeatures: false,
        prioritySupport: false,
        analyticsAccess: false
      },
      
      // UI state
      sidebarCollapsed: false,
      currentPage: null,
      
      // Actions
      setUser: (userData) => {
        set((state) => ({
          user: userData,
          isAuthenticated: !!userData,
          role: userData?.role || (userData ? 'member' : 'guest'),
          membershipTier: userData?.membershipTier || 'free'
        }));
        
        // Auto-update entitlements based on membership tier
        const currentState = get();
        currentState?.updateEntitlements(userData?.membershipTier || 'free');
      },
      
      setSession: (sessionData) => {
        set({ session: sessionData });
      },
      
      setLoading: (isLoading) => {
        set({ loading: isLoading });
      },
      
      setRole: (newRole) => {
        set({ role: newRole });
        
        // Auto-update entitlements based on role
        const currentState = get();
        if (newRole === 'admin') {
          currentState?.grantAllEntitlements();
        }
      },
      
      updateEntitlements: (tier) => {
        const tierEntitlements = {
          free: {
            aiChatMessages: 50,
            botHubAccess: false,
            communityFeatures: false,
            prioritySupport: false,
            analyticsAccess: false
          },
          paid: {
            aiChatMessages: 500,
            botHubAccess: true,
            communityFeatures: true,
            prioritySupport: true,
            analyticsAccess: true
          },
          elite: {
            aiChatMessages: -1, // unlimited
            botHubAccess: true,
            communityFeatures: true,
            prioritySupport: true,
            analyticsAccess: true
          }
        };
        
        set({ 
          entitlements: tierEntitlements?.[tier] || tierEntitlements?.free,
          membershipTier: tier
        });
      },
      
      grantMembership: (tier = 'paid') => {
        set((state) => ({
          membershipTier: tier,
          role: state?.role === 'guest' ? 'member' : state?.role
        }));
        
        const currentState = get();
        currentState?.updateEntitlements(tier);
        currentState?.unlockEntitlements();
      },
      
      unlockEntitlements: () => {
        set((state) => ({
          entitlements: {
            ...state?.entitlements,
            botHubAccess: true,
            communityFeatures: true
          }
        }));
      },
      
      grantAllEntitlements: () => {
        set({
          entitlements: {
            aiChatMessages: -1, // unlimited
            botHubAccess: true,
            communityFeatures: true,
            prioritySupport: true,
            analyticsAccess: true
          }
        });
      },
      
      // Role simulation for development
      simulateRole: (role) => {
        set({ role });
        
        if (role === 'admin') {
          const currentState = get();
          currentState?.grantAllEntitlements();
        } else if (role === 'member') {
          const currentState = get();
          currentState?.updateEntitlements('paid');
        } else {
          const currentState = get();
          currentState?.updateEntitlements('free');
        }
      },
      
      // UI actions
      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state?.sidebarCollapsed }));
      },
      
      setSidebarCollapsed: (collapsed) => {
        set({ sidebarCollapsed: collapsed });
      },
      
      setCurrentPage: (page) => {
        set({ currentPage: page });
      },
      
      // Auth actions
      signOut: () => {
        set({
          user: null,
          session: null,
          isAuthenticated: false,
          role: 'guest',
          membershipTier: 'free',
          entitlements: {
            aiChatMessages: 50,
            botHubAccess: false,
            communityFeatures: false,
            prioritySupport: false,
            analyticsAccess: false
          }
        });
      },
      
      // Utility functions
      hasPermission: (permission) => {
        const state = get();
        return state?.permissions?.includes(permission) || state?.role === 'admin';
      },
      
      canAccessFeature: (feature) => {
        const state = get();
        return state?.entitlements?.[feature] || state?.role === 'admin';
      },
      
      getRemainingMessages: () => {
        const state = get();
        return state?.entitlements?.aiChatMessages === -1 
          ? 'Unlimited' 
          : state?.entitlements?.aiChatMessages;
      }
    }),
    {
      name: 'recall-user-store',
      partialize: (state) => ({
        role: state?.role,
        membershipTier: state?.membershipTier,
        entitlements: state?.entitlements,
        sidebarCollapsed: state?.sidebarCollapsed
      })
    }
  )
);

export default useUserStore;

function getUserState() {
  return useUserStore?.getState();
}

export { getUserState };

function subscribeToUserStore(callback) {
  const unsubscribe = useUserStore?.subscribe(callback);
  return unsubscribe;
}

export { subscribeToUserStore };

function toggleUserRole(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: toggleUserRole is not implemented yet.', args);
  return null;
}

export { toggleUserRole };
function hasEntitlement(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: hasEntitlement is not implemented yet.', args);
  return null;
}

export { hasEntitlement };
function updateUserEntitlements(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: updateUserEntitlements is not implemented yet.', args);
  return null;
}

export { updateUserEntitlements };
function updateSubscriptionTier(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: updateSubscriptionTier is not implemented yet.', args);
  return null;
}

export { updateSubscriptionTier };